import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';

import { BiensImmobs } from 'app/models/biensImmobs';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { BiensImmobsBiensImmobs } from 'app/services/biens-immobs/biens-immobs.service';
import { AwscontrolService, UploadResponse, DeleteResponse, ImageUploadResponse } from 'app/services/aws/awscontrol.service';

type ImageUploadMode = 'direct' | 'imageproduit';

@Component({
  selector: 'app-modifier-biens-immob',
  templateUrl: './modifier-biens-immob.component.html',
  styleUrls: ['./modifier-biens-immob.component.scss']
})
export class ModifierBiensImmobComponent implements OnInit {
  // ✅ RÉFÉRENCE À L'ÉDITEUR contenteditable (WYSIWYG)
  @ViewChild('descFrEditor') descFrEditor?: ElementRef<HTMLElement>;

  // Form data
  biens: BiensImmobs;

  // File upload – single main image
  selectedFile: File | null = null;
  mainImage: string = '';

  // ==================== MODE + PREVIEW ====================
  imageUploadMode: ImageUploadMode = 'direct';
  previewOriginalUrl: string | null = null;
  previewProcessedUrl: string | null = null;
  isAwaitingValidation: boolean = false;
  // ========================================================

  // Agencies
  selectedAgencyName: string = '';

  // Agency search functionality
  public agencySearch: string = '';
  public filteredAgencies: any[] = [];
  public allAgencies: any[] = [];
  public isAgencySelectOpen: boolean = false;

  // Languages of the currently selected agency
  public selectedAgencyLanguages: string[] = [];
  public showArabicFields: boolean = false;
  public showEnglishFields: boolean = false;

  // Categories
  public categoryInput: string = '';

  // Other
  public biensImmob: any;
  public idBienImmobs: any;
  public agencyId: string | null = null;
  dataSource = new MatTableDataSource();

  // Upload states
  public isUploading: boolean = false;
  public uploadProgress: number = 0;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private _location: Location,
    private agenciesService: AgenciesService,
    private bienImmobService: BiensImmobsBiensImmobs,
    private awsService: AwscontrolService
  ) {
    this.biens = new BiensImmobs();
  }

  ngOnInit(): void {
    this.idBienImmobs = this.activatedRoute.snapshot.paramMap.get('id');
    this.getData();
    this.loadAgencies();
  }

  getData() {
    this.bienImmobService.getBiensImmobs(this.idBienImmobs).subscribe((data: any) => {
      console.log('Bien data loaded:', data);
      this.biens = data;

      if (data.imageUrl) {
        this.mainImage = data.imageUrl;
      }

      if (data.agencyName) {
        if (typeof data.agencyName === 'object') {
          const agencyObj = data.agencyName as any;
          this.selectedAgencyName = agencyObj.name || '';
        } else {
          this.selectedAgencyName = data.agencyName;
        }
      }

      if (data.user?.id) {
        this.agencyId = data.user.id.toString();
      } else if (data.userId) {
        this.agencyId = data.userId.toString();
      } else if (data.agencyId) {
        this.agencyId = data.agencyId.toString();
      }

      if (!this.biens.categoryList) {
        this.biens.categoryList = [];
      }

      if (data.nameArabe) this.biens.nameArabe = data.nameArabe;
      if (data.nameAnglais) this.biens.nameAnglais = data.nameAnglais;
      if (data.nomCommercialArabe) this.biens.nomCommercialArabe = data.nomCommercialArabe;

      this.applyLanguagesForSelectedAgency();

      console.log('Property loaded successfully:', this.biens);
      console.log('Agency ID for navigation:', this.agencyId);

      // 🔥 Remplir l'éditeur après chargement
      this.applyRichTextAfterRender();
    });
  }

  loadAgencies() {
    this.agenciesService.getList().subscribe({
      next: (data: any) => {
        let agencies: any[] = [];

        if (Array.isArray(data)) {
          agencies = data;
        } else if (data && typeof data === 'object' && data.name) {
          agencies = [data];
        } else {
          agencies = [];
        }

        this.allAgencies = agencies
          .filter(agency =>
            agency &&
            agency.name &&
            typeof agency.name === 'string' &&
            (agency.role === 'meilleurs-biens' || agency.role === 'agence-immobilieres')
          )
          .map(agency => ({
            id: agency.id,
            name: agency.name.trim(),
            availableLanguages: agency.availableLanguages || []
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        this.filteredAgencies = [...this.allAgencies];
        this.applyLanguagesForSelectedAgency();
      },
      error: (error) => {
        console.error('Error loading agencies:', error);
        this.allAgencies = [];
        this.filteredAgencies = [];
      }
    });
  }

  private applyLanguagesForSelectedAgency(): void {
    if (!this.selectedAgencyName || this.allAgencies.length === 0) return;

    const found = this.allAgencies.find(a => a.name === this.selectedAgencyName);
    if (!found) return;

    this.selectedAgencyLanguages = found.availableLanguages || [];
    this.showArabicFields = this.selectedAgencyLanguages.includes('AR');
    this.showEnglishFields = this.selectedAgencyLanguages.includes('EN');

    if (!this.showArabicFields) {
      this.biens.nameArabe = '';
      this.biens.nomCommercialArabe = '';
    }
    if (!this.showEnglishFields) {
      this.biens.nameAnglais = '';
    }

    const idx = this.allAgencies.findIndex(a => a.id === found.id);
    if (idx > 0) {
      const [selected] = this.allAgencies.splice(idx, 1);
      this.allAgencies.unshift(selected);
    }
    this.filteredAgencies = [...this.allAgencies];
  }

  /* ============================================================
     ✅ SYNC DE L'ÉDITEUR contenteditable
     ============================================================ */
  private applyRichTextAfterRender(): void {
    setTimeout(() => this.syncEditor(), 0);
    setTimeout(() => this.syncEditor(), 100);
    setTimeout(() => this.syncEditor(), 300);
  }

  private syncEditor(): void {
    if (this.descFrEditor) {
      const html = this.biens.description || '';
      if (this.descFrEditor.nativeElement.innerHTML !== html) {
        this.descFrEditor.nativeElement.innerHTML = html;
      }
    }
  }

  /* ============================================================
     ✅ HANDLER (input) DE L'ÉDITEUR
     ============================================================ */
  onDescInput(): void {
    if (this.descFrEditor) {
      this.biens.description = this.descFrEditor.nativeElement.innerHTML;
    }
  }

  /* ============================================================
     BOLD — seule action de rich text exposée
     ============================================================ */
  makeBold(): void {
    try {
      document.execCommand('bold', false);
    } catch (e) {
      console.warn('execCommand("bold") failed:', e);
    }
  }

  // Categories management
  addCategory(): void {
    if (this.categoryInput && this.categoryInput.trim() !== '') {
      if (!this.biens.categoryList) {
        this.biens.categoryList = [];
      }
      this.biens.categoryList.push(this.categoryInput.trim());
      this.categoryInput = '';
    }
  }

  removeCategory(category: string): void {
    if (this.biens.categoryList) {
      const index = this.biens.categoryList.indexOf(category);
      if (index >= 0) {
        this.biens.categoryList.splice(index, 1);
      }
    }
  }

  // Agency search
  filterAgencies(): void {
    if (!this.agencySearch || this.agencySearch.trim() === '') {
      this.filteredAgencies = [...this.allAgencies];
    } else {
      const searchTerm = this.agencySearch.toLowerCase().trim();
      this.filteredAgencies = this.allAgencies.filter(agency => {
        if (!agency || !agency.name) return false;
        return agency.name.toLowerCase().includes(searchTerm);
      });
    }
  }

  clearAgencySearch(): void {
    this.agencySearch = '';
    this.filteredAgencies = [...this.allAgencies];
  }

  onAgencySelectOpened() {
    this.isAgencySelectOpen = true;
    this.agencySearch = '';
    this.filterAgencies();
  }

  onAgencySelectClosed() {
    this.isAgencySelectOpen = false;
  }

  selectAgency(agency: any): void {
    this.selectedAgencyName = agency.name;
    this.selectedAgencyLanguages = agency.availableLanguages || [];

    this.showArabicFields = this.selectedAgencyLanguages.includes('AR');
    this.showEnglishFields = this.selectedAgencyLanguages.includes('EN');

    if (!this.showArabicFields) {
      this.biens.nameArabe = '';
      this.biens.nomCommercialArabe = '';
    }
    if (!this.showEnglishFields) {
      this.biens.nameAnglais = '';
    }

    const idx = this.allAgencies.findIndex(a => a.id === agency.id);
    if (idx > 0) {
      const [selected] = this.allAgencies.splice(idx, 1);
      this.allAgencies.unshift(selected);
    }
    this.filteredAgencies = [...this.allAgencies];

    // Re-sync éditeur
    this.applyRichTextAfterRender();
  }

  // ==================== HELPERS ====================
  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire({ icon: 'warning', title: 'Format non supporté', text: 'JPEG, PNG, JPG, GIF, WEBP uniquement', timer: 2000 });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({ icon: 'warning', title: 'Fichier trop volumineux', text: 'Max 5MB', timer: 2000 });
      return false;
    }
    return true;
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
  // =================================================

  setUploadMode(mode: ImageUploadMode): void {
    if (this.isUploading) return;
    if (this.isAwaitingValidation) this.cancelPreview(true);
    this.imageUploadMode = mode;
  }

  // ========== MAIN IMAGE UPLOAD (SINGLE) ==========
  selectFileimg(event: any): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;

    if (!this.validateImageFile(file)) {
      input.value = '';
      return;
    }

    this.selectedFile = file;

    this.fileToDataUrl(file).then(d => {
      this.previewOriginalUrl = d;
    });

    if (this.imageUploadMode === 'direct') {
      this.uploadMainImage();
    } else {
      this.uploadMainViaImageproduit(file, input);
    }

    input.value = '';
  }

  async uploadMainImage(): Promise<void> {
    if (!this.selectedFile) return;

    this.isUploading = true;
    this.uploadProgress = 0;

    Swal.fire({
      title: 'Téléchargement en cours...',
      html: 'Upload de l\'image principale',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    try {
      const folder = 'biens-immobiliers';
      const response = await this.awsService.uploadFile(this.selectedFile, folder).toPromise();

      Swal.close();

      if (response.success && response.urls && response.urls.length > 0) {
        const uploadedUrl = response.urls[0];
        this.mainImage = uploadedUrl;
        localStorage.setItem('bienImage', JSON.stringify(uploadedUrl));
        this.previewOriginalUrl = null;

        Swal.fire({
          icon: "success",
          title: "Image mise à jour",
          text: "L'image principale a été téléchargée avec succès",
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (err: any) {
      console.error('Error uploading main image:', err);
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Erreur de téléchargement",
        text: "Une erreur est survenue lors du téléchargement de l'image",
      });
      this.previewOriginalUrl = null;
    } finally {
      this.isUploading = false;
      this.uploadProgress = 0;
      this.selectedFile = null;
    }
  }

  private uploadMainViaImageproduit(file: File, input: HTMLInputElement): void {
    this.isUploading = true;
    this.isAwaitingValidation = false;
    this.previewProcessedUrl = null;

    this.awsService.imageproduit([file], 'biens-immobiliers').subscribe({
      next: (response: ImageUploadResponse) => {
        this.isUploading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.previewProcessedUrl = response.urls[0];
          this.isAwaitingValidation = true;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: (response.errors && response.errors[0]) || 'Échec du traitement',
            confirmButtonColor: '#f44336'
          });
          this.previewOriginalUrl = null;
        }
      },
      error: () => {
        this.isUploading = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter l\'image principale', confirmButtonColor: '#f44336' });
        this.previewOriginalUrl = null;
      }
    });
  }

  validatePreview(): void {
    if (!this.previewProcessedUrl) return;
    this.mainImage = this.previewProcessedUrl;
    localStorage.setItem('bienImage', JSON.stringify(this.previewProcessedUrl));
    this.previewOriginalUrl = null;
    this.previewProcessedUrl = null;
    this.isAwaitingValidation = false;
    this.selectedFile = null;
    Swal.fire({ icon: 'success', title: 'Image validée', timer: 1500, showConfirmButton: false });
  }

  cancelPreview(silent: boolean = false): void {
    const url = this.previewProcessedUrl;
    const doCancel = () => {
      if (url) this.awsService.deleteFile(url).subscribe({ error: () => {} });
      this.previewOriginalUrl = null;
      this.previewProcessedUrl = null;
      this.isAwaitingValidation = false;
      this.selectedFile = null;
    };
    if (silent) { doCancel(); return; }
    Swal.fire({
      title: 'Refuser cette image ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }

  async removeMainImage(): Promise<void> {
    if (this.isAwaitingValidation) {
      this.cancelPreview();
      return;
    }
    if (!this.mainImage) return;

    const result = await Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Voulez-vous vraiment supprimer cette image?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        await this.awsService.deleteFile(this.mainImage).toPromise();
        console.log('Image deleted from AWS:', this.mainImage);
      } catch (error) {
        console.warn('Could not delete image from AWS:', error);
      }

      this.mainImage = '';
      localStorage.removeItem('bienImage');

      Swal.fire({
        icon: "success",
        title: "Image supprimée",
        text: "L'image a été supprimée avec succès",
        timer: 2000,
        showConfirmButton: false
      });
    }
  }

  validateYouTubeUrl(url: string): boolean {
    if (!url) return true;
    const youtubePattern = /^(https?\:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    return youtubePattern.test(url);
  }

  private goToAgencyBiensList(): void {
    if (this.agencyId) {
      this.router.navigate(['/list-agence-biens', this.agencyId]);
    } else {
      this._location.back();
    }
  }

  updateProperty(): void {
    if (this.isAwaitingValidation) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation requise',
        text: 'Veuillez valider ou refuser l\'image avant de continuer.'
      });
      return;
    }

    if (this.isUploading) {
      Swal.fire({
        icon: 'warning',
        title: 'Téléchargement en cours',
        text: 'Veuillez attendre la fin du téléchargement de l\'image'
      });
      return;
    }

    if (!this.biens.name || !this.biens.description || !this.selectedAgencyName) {
      Swal.fire({
        icon: 'warning',
        title: 'Champs requis',
        text: 'Veuillez remplir tous les champs obligatoires (Nom, Description, Agence)'
      });
      return;
    }

    if (this.biens.videoUrlBien && !this.validateYouTubeUrl(this.biens.videoUrlBien)) {
      Swal.fire({
        icon: 'error',
        title: 'URL YouTube invalide',
        text: 'Veuillez entrer une URL YouTube valide'
      });
      return;
    }

    if (!this.mainImage) {
      Swal.fire({
        icon: 'warning',
        title: 'Image requise',
        text: 'Veuillez ajouter une image du bien'
      });
      return;
    }

    // 🔥 Récupérer la valeur depuis l'éditeur contenteditable
    this.onDescInput();

    const updateData = {
      "name": this.biens.name,
      "nameArabe": this.showArabicFields ? (this.biens.nameArabe || '') : '',
      "nameAnglais": this.showEnglishFields ? (this.biens.nameAnglais || '') : '',
      "description": this.biens.description,
      "prix": this.biens.prix,
      "agencyName": this.selectedAgencyName,
      "categoryList": this.biens.categoryList || [],
      "imageUrl": this.mainImage,
      "videoUrlBien": this.biens.videoUrlBien,
      "nomCommercial": this.biens.nomCommercial || '',
      "nomCommercialArabe": this.showArabicFields ? (this.biens.nomCommercialArabe || '') : '',
      "numCommercial": this.biens.numCommercial || '',
      "emailCommercial": this.biens.emailCommercial || ''
    };

    console.log('Updating property:', updateData);

    Swal.fire({
      title: 'Mise à jour en cours...',
      text: 'Veuillez patienter',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    this.bienImmobService.updateBiensImmobs(this.idBienImmobs, updateData).subscribe(
      (response: any) => {
        Swal.close();
        Swal.fire({
          icon: "success",
          title: "Succès!",
          text: "Le bien immobilier a été mis à jour avec succès",
          confirmButtonText: 'OK'
        }).then(() => {
          this.goToAgencyBiensList();
        });
      },
      (error) => {
        Swal.close();
        console.error('Error updating property:', error);

        let errorMessage = 'Échec de la mise à jour du bien immobilier';
        if (error.status === 400) {
          errorMessage = 'Données invalides. Veuillez vérifier les informations saisies.';
        } else if (error.status === 401) {
          errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        } else if (error.status === 500) {
          errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        Swal.fire({
          icon: "error",
          title: "Erreur",
          text: errorMessage,
        });
      }
    );
  }

  onCancel(): void {
    Swal.fire({
      title: 'Annuler les modifications?',
      text: 'Les modifications non enregistrées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, annuler',
      cancelButtonText: 'Continuer l\'édition'
    }).then((result) => {
      if (result.isConfirmed) {
        this.goToAgencyBiensList();
      }
    });
  }

  async checkAWSServiceHealth(): Promise<void> {
    try {
      const health = await this.awsService.checkHealth().toPromise();
      Swal.fire({
        icon: 'success',
        title: 'Service AWS Connecté',
        html: `
          <div style="text-align: left;">
            <p><strong>Statut:</strong> ${health.status}</p>
            <p><strong>Bucket:</strong> ${health.bucket}</p>
            <p><strong>Région:</strong> ${health.region}</p>
            <p><strong>Dernière vérification:</strong> ${health.timestamp}</p>
          </div>
        `,
        confirmButtonColor: '#3f51b5'
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Service AWS non disponible',
        text: 'Impossible de vérifier la connexion au service AWS'
      });
    }
  }

  getFileNameFromUrl(url: string): string {
    return this.awsService.extractFileName(url);
  }
}